import { Navigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import { Snackbar } from '@/shared/components/Snackbar';
import { Textarea, TextField } from '@/shared/components/TextField';
import { ToggleButton } from '@/shared/components/ToggleButton';
import CreateLayout from '@/shared/layout/createLayout';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { SERVICE_TYPE_LABELS } from '../../constants/labels';
import { useEditServiceType } from '../../hooks/useEditServiceType';
import { useUpdateServiceTypeStatus } from '../../hooks/useUpdateServiceTypeStatus';
import { ServiceTypeStatus } from '../../types/serviceTypeTypes';
import { getServiceTypeStatusDialogConfig } from '../serviceTypeList/serviceTypeStatusDialogConfig';
import ViewServiceTypeErrorScreen from '../viewServiceType/ViewServiceTypeErrorScreen';

const EditServiceType: React.FC = () => {
  const {
    serviceTypeId,
    formData,
    errors,
    status,
    setStatus,
    isLoading,
    isSubmitting,
    loadError,
    notFound,
    hasChanges,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleServiceNameChange,
    handleDescriptionChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  } = useEditServiceType();

  const {
    selectedServiceType,
    isDialogOpen,
    isSubmitting: isStatusSubmitting,
    handleOpenDialog: openStatusDialog,
    handleDialogClose: closeStatusDialog,
    handleConfirm: handleConfirmStatusUpdate,
    showSuccessSnackbar: showStatusSuccessSnackbar,
    setShowSuccessSnackbar: setShowStatusSuccessSnackbar,
    successMessage: statusSuccessMessage,
    showErrorSnackbar: showStatusErrorSnackbar,
    setShowErrorSnackbar: setShowStatusErrorSnackbar,
    errorMessage: statusErrorMessage,
  } = useUpdateServiceTypeStatus({
    onSuccess: ({ status: nextStatus }) => setStatus(nextStatus),
  });

  const handleStatusToggle = () => {
    if (!serviceTypeId) {
      return;
    }
    openStatusDialog({
      id: serviceTypeId,
      service_name: formData.serviceName,
      status,
    });
  };

  const isStatusToggleDisabled =
    isSubmitting || isLoading || isStatusSubmitting;

  const Header = () => (
    <div className="flex items-center justify-between">
      <p className="text-titleLarge text-neutral-10">
        {SERVICE_TYPE_LABELS.EDIT_TITLE}
      </p>
      <div className="flex items-center gap-2 mr-5">
        <p className="text-[12px] text-on-surface-variant">
          {SERVICE_TYPE_LABELS.FIELD_STATUS}
        </p>
        <div
          className="flex min-w-[110px] items-center"
          aria-label={SERVICE_TYPE_LABELS.FIELD_STATUS}
          data-testid="service-type-status-toggle"
        >
          <div className="leading-none">
            <ToggleButton
              checked={status === ServiceTypeStatus.Active}
              onChange={handleStatusToggle}
              label={
                status === ServiceTypeStatus.Active
                  ? SERVICE_TYPE_LABELS.STATUS_ACTIVE
                  : SERVICE_TYPE_LABELS.STATUS_INACTIVE
              }
              size="XSmall"
              disabled={isStatusToggleDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (loadError) {
    return <ViewServiceTypeErrorScreen />;
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={Header}
        submitButtonLabel={SERVICE_TYPE_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={SERVICE_TYPE_LABELS.BUTTON_SAVE_LOADING}
        cancelButtonLabel={SERVICE_TYPE_LABELS.BUTTON_CANCEL}
        submitButtonDisabled={isSubmitting || !hasChanges}
      >
        <div className="flex flex-col gap-2">
          <p className="text-[18px] text-on-surface-variant">
            {SERVICE_TYPE_LABELS.SECTION_DETAILS}
          </p>
          <div className="flex flex-col gap-8">
            <div className="flex gap-4">
              <TextField
                type="text"
                inputMode="text"
                label={SERVICE_TYPE_LABELS.FIELD_NAME}
                maxLength={50}
                width={'50%'}
                autoFocus
                autoComplete="off"
                value={formData.serviceName}
                onChange={handleServiceNameChange}
                error={Boolean(errors.serviceName)}
                errorMessage={errors.serviceName ?? ''}
                disabled={isSubmitting}
              />
            </div>
            <Textarea
              label={SERVICE_TYPE_LABELS.FIELD_DESCRIPTION}
              maxLength={500}
              fullWidth
              rows={4}
              value={formData.description}
              onChange={handleDescriptionChange}
              error={Boolean(errors.description)}
              errorMessage={errors.description ?? ''}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </CreateLayout>

      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={() => setShowStatusSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowStatusErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />

      {selectedServiceType && (
        <UpdateStatusDialog
          open={isDialogOpen}
          onClose={closeStatusDialog}
          onConfirm={handleConfirmStatusUpdate}
          isSubmitting={isStatusSubmitting}
          {...getServiceTypeStatusDialogConfig(
            selectedServiceType.service_name,
            selectedServiceType.status === ServiceTypeStatus.Active
          )}
        />
      )}
    </>
  );
};

export default EditServiceType;
